/**
 * Firefox-targeted store: opening goes through a file picker, saving through a
 * download. There is no in-place write and no permission to request — the
 * friction lives in the download flow, not in an authorisation prompt.
 */

import { serialize } from '../model/document';
import type { ChronoDocument } from '../model/types';
import { readDocumentFile, type DocumentStore, type OpenResult } from './store';

/** Long enough for the browser to have taken the blob, short enough not to leak. */
const REVOKE_DELAY_MS = 10_000;

/** Hands a blob to the browser's download flow — the only way to write a file here. */
export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);

  // Firefox has historically required the anchor to be in the document for a
  // programmatic click to start the download.
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();

  // Revoking straight away can abort the download that has just started.
  setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
}

export class DownloadStore implements DocumentStore {
  readonly canSaveInPlace = false;

  open(): Promise<OpenResult> {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'application/json,.json';
      input.style.display = 'none';

      const settle = (result: Promise<OpenResult> | OpenResult) => {
        input.remove();
        void Promise.resolve(result).then(resolve);
      };

      input.addEventListener('change', () => {
        const file = input.files?.[0];
        settle(file ? readDocumentFile(file) : { kind: 'cancelled' });
      });
      // Not fired by every browser version; the picker is simply left open then.
      input.addEventListener('cancel', () => settle({ kind: 'cancelled' }));

      document.body.append(input);
      input.click();
    });
  }

  async save(doc: ChronoDocument, filename: string): Promise<void> {
    triggerDownload(new Blob([serialize(doc)], { type: 'application/json' }), filename);
  }
}
